import React, { useEffect, useState } from 'react'
import axios from 'axios'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	MenuItem,
	Typography,
	IconButton
} from '@material-ui/core'
import iziToast from 'izitoast'
import { useStoreon } from 'storeon/react'
import { Close } from '@material-ui/icons'

interface Section {
	_id: string
	name: string
}

interface SubSection {
	subSectionId: string
	name: string
	description: string
	sectionName?: string
	questionCount?: number
	_id?: string
}

interface EditSubSectionModalProps {
	open: boolean
	onClose: () => void
	rowData: SubSection
	onUpdated: (updatedRow: SubSection) => void
	onSuccess?: () => void
}

const EditSubSectionModal: React.FC<EditSubSectionModalProps> = ({
	open,
	onClose,
	rowData,
	onUpdated,
	onSuccess
}) => {
	const [sections, setSections] = useState<Section[]>([])
	const [sectionId, setSectionId] = useState('')
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [loading, setLoading] = useState(false)
	const { token } = useStoreon('token')
	// const modifiedBy = '6657227e295423a9922a813e'

	useEffect(() => {
		if (open) {
			axios
				.get(`${process.env.REACT_APP_API_URL}/api/v1/custom/getSections`, {
					headers: {
						Authorization: ` ${token}`
					}
				})
				.then(res => {
					setSections(res.data)
					if (rowData.sectionName) {
						const matchedSection = res.data.find(
							(sec: Section) => sec.name === rowData.sectionName
						)
						if (matchedSection) {
							setSectionId(matchedSection._id)
						}
					}
				})
				.catch()

			setName(rowData.name)
			setDescription(rowData.description)
		}
	}, [open, rowData])

	const handleUpdate = async () => {
		if (!name || !description) {
			iziToast.warning({
				message: 'Please fill out all fields'
			})
			return
		}

		setLoading(true)
		try {
			await axios.put(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/updateSubSection`,
				{
					subSectionId: rowData._id,
					name,
					description,

					// modifiedBy,
					...(sectionId && { sectionId })
				},
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			onUpdated({
				...rowData,
				name,
				description,
				sectionName: sections.find(s => s._id === sectionId)?.name || rowData.sectionName,
				questionCount: rowData.questionCount ?? 0
			})

			iziToast.success({
				message: 'SubSection updated successfully!'
			})

			onSuccess?.()
			onClose()
		} catch (error: any) {
			console.error('Update error:', error)
			iziToast.error({
				message: 'Failed to update SubSection'
			})
		} finally {
			setLoading(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle
				disableTypography
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					backgroundColor: 'rgb(125, 110, 241)',
					height: '50px',
					color: 'white',
					padding: '0 16px'
				}}
			>
				<h2 style={{ margin: 0, fontSize: '1.25rem' }}>Edit SubSection</h2>
				{/* <IconButton onClick={onClose} style={{ marginRight: '-18px' }}> */}
				<Close style={{ color: 'white', cursor: 'pointer' }} onClick={onClose} />
				{/* </IconButton> */}
			</DialogTitle>

			<DialogContent>
				<Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
					Section Name
				</Typography>
				<TextField
					autoFocus
					margin="dense"
					select
					fullWidth
					value={sectionId}
					onChange={e => setSectionId(e.target.value)}
					SelectProps={{
						MenuProps: {
							getContentAnchorEl: null,
							anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
							transformOrigin: { vertical: 'top', horizontal: 'left' },
							PaperProps: {
								style: { maxHeight: 200, overflowY: 'auto' }
							}
						}
					}}
				>
					<MenuItem value="">Select Section</MenuItem>
					{sections.map(section => (
						<MenuItem key={section._id} value={section._id}>
							{section.name}
						</MenuItem>
					))}
				</TextField>

				<Typography variant="body2" fontWeight="bold" style={{ marginTop: '20px' }}>
					SubSection Name
				</Typography>
				<TextField
					autoFocus
					type="text"
					fullWidth
					value={name}
					onChange={e => setName(e.target.value)}
					margin="dense"
				/>

				<Typography variant="body2" fontWeight="bold" style={{ marginTop: '20px' }}>
					Description
				</Typography>
				<TextField
					autoFocus
					margin="dense"
					type="text"
					fullWidth
					multiline
					rows={3}
					value={description}
					onChange={e => setDescription(e.target.value)}
					placeholder="Enter description"
				/>
			</DialogContent>
			<DialogActions style={{ padding: '16px' }}>
				<Button onClick={onClose} variant="outlined">
					Cancel
				</Button>
				<Button onClick={handleUpdate} disabled={loading} variant="contained">
					{loading ? 'Updating...' : 'Update'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default EditSubSectionModal
